import {
  getShiftedDate,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { blockDateSpanHasLockedDay } from "./scheduleBlockSelectors";

/**
 * Local date keys from inclusive `[startDay, endDay]` (order-independent).
 *
 * @param {Date} startDay
 * @param {Date} endDay
 * @returns {string[]}
 */
function enumerateInclusiveDayKeysBetween(startDay, endDay) {
  const a = toLocalCalendarDay(startDay);
  const b = toLocalCalendarDay(endDay);
  const start = toLocalDateKey(a) <= toLocalDateKey(b) ? a : b;
  const end = toLocalDateKey(a) <= toLocalDateKey(b) ? b : a;
  const keys = [];
  let d = new Date(start);
  while (toLocalDateKey(d) <= toLocalDateKey(end)) {
    keys.push(toLocalDateKey(d));
    d = getShiftedDate(d, 1);
  }
  return keys;
}

/**
 * True when any day in inclusive `[startDate, endDate]` is locked.
 *
 * @param {Date|null|undefined} startDate
 * @param {Date|null|undefined} endDate
 * @param {Record<string, boolean>|null|undefined} dayLocks
 * @returns {boolean}
 */
export function dateRangeHasLockedDay(startDate, endDate, dayLocks) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    return false;
  }
  const start = toLocalCalendarDay(startDate);
  const end = toLocalCalendarDay(endDate);
  const minDay = toLocalDateKey(start) <= toLocalDateKey(end) ? start : end;
  const maxDay = toLocalDateKey(start) <= toLocalDateKey(end) ? end : start;
  return blockDateSpanHasLockedDay({ startDate: minDay, endDate: maxDay }, dayLocks);
}

/**
 * True when a block edge resize changes through any locked day.
 *
 * This checks only the resized edge delta (inclusive old↔new edge date),
 * not the full resulting block span.
 *
 * @param {Object|null|undefined} beforeBlock
 * @param {Object|null|undefined} nextBlock
 * @param {'start'|'end'} edge
 * @param {Record<string, boolean>|null|undefined} dayLocks
 * @returns {boolean}
 */
export function blockResizeTouchesLockedDay(beforeBlock, nextBlock, edge, dayLocks) {
  if (!beforeBlock || !nextBlock) {
    return false;
  }
  const beforeEdge = edge === "start" ? beforeBlock.startDate : beforeBlock.endDate;
  const nextEdge = edge === "start" ? nextBlock.startDate : nextBlock.endDate;
  return dateRangeHasLockedDay(beforeEdge, nextEdge, dayLocks);
}

/**
 * True when any **added or removed** override-strip day (symmetric difference
 * between previous and next inclusive ranges) is locked.
 *
 * Does not reject when a locked day remains inside both spans unchanged.
 *
 * @param {Object} params
 * @param {Date} params.previousStartDay
 * @param {Date} params.previousEndDay
 * @param {Date} params.nextStartDay
 * @param {Date} params.nextEndDay
 * @param {Record<string, boolean>|null|undefined} params.dayLocks
 * @returns {boolean}
 */
export function overrideRangeResizeChangedDaysTouchLockedDay({
  previousStartDay,
  previousEndDay,
  nextStartDay,
  nextEndDay,
  dayLocks,
}) {
  if (dayLocks == null) {
    return false;
  }
  const prevKeys = new Set(
    enumerateInclusiveDayKeysBetween(previousStartDay, previousEndDay)
  );
  const nextKeys = new Set(
    enumerateInclusiveDayKeysBetween(nextStartDay, nextEndDay)
  );
  for (const k of prevKeys) {
    if (!nextKeys.has(k) && dayLocks[k]) {
      return true;
    }
  }
  for (const k of nextKeys) {
    if (!prevKeys.has(k) && dayLocks[k]) {
      return true;
    }
  }
  return false;
}
